import React, {
  useMemo
} from "react";

import {
  Content,
  Grid,
  GridItem,
  Hero,
  Label,
  PageSection,
  Stack,
  StackItem,
  Flex,
  FlexItem
} from "@patternfly/react-core";

import './EducationDirectionHero.css'

const getImageSource = (image) => {
  if (
    typeof image === "string" &&
    image.trim()
  ) {
    return image;
  }

  if (
    image &&
    typeof image.src === "string" &&
    image.src.trim()
  ) {
    return image.src;
  }

  return null;
};

const EducationDirectionHero = ({
  direction,
  image: selectedImage,
  titleComponent = "h1",
  professionSelector = null,
  footer = null,
  isCarouselSlide = false
}) => {
  const {
    schoolType,
    audience,
    duration,
    title,
    specialization,
    extendedSubject,
    professionNumber,
    summary,
    hero = {}
  } = direction;

  const fallbackImage = useMemo(() => {
    const heroImages =
      Array.isArray(hero.images)
        ? hero.images
        : [];

    const professionImages =
      Array.isArray(direction.professions)
        ? direction.professions.flatMap(
            (profession) => {
              if (Array.isArray(profession.images)) {
                return profession.images;
              }

              if (Array.isArray(profession.hero?.images)) {
                return profession.hero.images;
              }

              if (profession.image) {
                return [profession.image];
              }

              return [];
            }
          )
        : [];

    const availableImages = [
      ...heroImages,
      ...professionImages
    ]
      .map(getImageSource)
      .filter(Boolean);

    if (!availableImages.length) {
      return null;
    }

    return availableImages[
      Math.floor(
        Math.random() * availableImages.length
      )
    ];
  }, [
    direction.id,
    direction.professions,
    hero.images
  ]);

  const image =
    getImageSource(selectedImage) ??
    fallbackImage ??
    getImageSource(hero.image);

  const displayedAudience =
    isCarouselSlide
      ? direction.carousel?.audience ?? audience
      : audience;

  const displayedSummary =
    isCarouselSlide
      ? direction.carousel?.summary ?? summary
      : summary;
  return (
    <Hero
      className="education-direction-hero"
      isGlass
      bodyWidth="100%"
      bodyMaxWidth="100%"
      gradientLight={{
        stop1: "rgba(235, 242, 255, 0.98)",
        stop2: "rgba(235, 242, 255, 0.88)",
        stop3: "rgba(235, 242, 255, 0.35)"
      }}
      gradientDark={{
        stop1: "rgba(8, 13, 31, 0.98)",
        stop2: "rgba(22, 34, 77, 0.9)",
        stop3: "rgba(36, 59, 120, 0.4)"
      }}
    >
      {image && (
        <div
          className="education-direction-hero__background-image"
          style={{
            backgroundImage: `url("${image}")`
          }}
          aria-hidden="true"
        />
      )}

      <PageSection
        className="education-direction-hero__content"
        isWidthLimited
      >
        <Grid hasGutter>
          <GridItem
            span={12}
            md={image ? 8 : 10}
            mdOffset={image ? undefined : 1}
            lg={image ? 7 : 8}
            lgOffset={image ? undefined : 2}
          >
            <Stack hasGutter>
              <StackItem>
                <Label
                  color="blue"
                  variant="outline"
                >
                  {schoolType}
                </Label>
              </StackItem>

              <StackItem>
                <Stack hasGutter>
                  <StackItem>
                    <Flex
                      alignItems={{
                        default: "alignItemsCenter"
                      }}
                      justifyContent={{
                        default: "justifyContentFlexStart"
                      }}
                      flexWrap={{
                        default: "wrap"
                      }}
                      gap={{
                        default: "gapXl"
                      }}
                    >
                      <FlexItem>
                        <Content component={titleComponent}>
                          {title}
                        </Content>
                      </FlexItem>

                      {professionSelector && (
                        <FlexItem className="education-direction-hero__profession-selector">
                          {professionSelector}
                        </FlexItem>
                      )}

                      {specialization && (
                        <FlexItem>
                          <Label
                            color="cyan"
                            variant="outline"
                          >
                            Specjalność: <strong>{specialization}</strong>
                          </Label>
                        </FlexItem>
                      )}
                    </Flex>
                  </StackItem>

                  {!isCarouselSlide &&
                    (specialization || extendedSubject) && (
                      <StackItem>
                        <Flex
                          alignItems={{
                            default: "alignItemsCenter"
                          }}
                          flexWrap={{
                            default: "wrap"
                          }}
                          gap={{
                            default: "gapLg"
                          }}
                        >
                          {extendedSubject && (
                            <FlexItem>
                              <small>
                                Przedmiot rozszerzony:{" "}
                                <strong>
                                  {extendedSubject}
                                </strong>
                              </small>
                            </FlexItem>
                          )}
                        </Flex>
                      </StackItem>
                    )}

                  <StackItem>
                    <Content component="p">
                      {displayedSummary}
                    </Content>
                  </StackItem>
                </Stack>
              </StackItem>
              
              <StackItem>
                <Grid hasGutter>
                  <GridItem
                    span={12}
                    sm={4}
                  >
                    <Content>
                      <small>Dla kogo</small>

                      <p>
                        <strong>{displayedAudience}</strong>
                      </p>
                    </Content>
                  </GridItem>

                  <GridItem
                    span={12}
                    sm={3}
                  >
                    <Content>
                      <small>Czas nauki</small>

                      <p>
                        <strong>{duration}</strong>
                      </p>
                    </Content>
                  </GridItem>

                  <GridItem
                    span={12}
                    sm={5}
                  >
                    <Content>
                      <small>Numer zawodu</small>

                      <p>
                        <strong>{professionNumber}</strong>
                      </p>
                    </Content>
                  </GridItem>
                </Grid>
              </StackItem>
              {footer && (
                <StackItem>
                  {footer}
                </StackItem>
              )}
            </Stack>
          </GridItem>
        </Grid>
      </PageSection>
    </Hero>
  );
};


export default EducationDirectionHero;